import { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store, persistor } from './store/store';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminGuard from './components/auth/AdminGuard';
import { App as CapApp } from '@capacitor/app';

// 첫 화면(랜딩/로그인) 진입 경로는 왕복(RTT) 최소화를 위해 eager 유지
import Login from './pages/Login';
import Register from './pages/Register';
import FindAccount from './pages/FindAccount';
import ResetPassword from './pages/ResetPassword';
import Landing from './pages/Landing';

// 그 외 페이지는 초기 번들 축소를 위해 React.lazy로 지연 로딩
import {
  Home, CharacterCreate, Map, StageDetail, Week0Page, Week1Page, ModuleDetailPage, Skills,
  GradeInfo, Settings, CustomExercises, CharacterProfile, ProfileEdit, LevelTestResult,
  WorkoutHistoryPage, WorkoutStatsPage, BodyTwin, Achievements, GoalSelect, InitialLevelTest,
  Programs, ProgramDetail, MyProgram, PublicProfile, BoardsHome, FreeBoard, NoticeBoard,
  MapBoard, BoardPostDetail, Shop, Leaderboard, GymRegister, WorkoutRecord, DataTransfer,
  ChargePoints, PaymentSuccess, PaymentFail, VideoAnalysisPage, VideoAnalysisDetail, Fragments,
  TrainerDashboard, TrainerLogin, SubscriberList,
  AdminDashboard, AdminLogin, TrainerVerification, UserManagement, ProductCatalogManagement,
  FragmentRedemptionManagement, MapConfig, PaymentManagement, AdminSkillReviewPage,
} from './pages/lazyPages';

import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { BaralProvider } from './contexts/BaralContext';
import { OfflineIndicator } from './components/common/OfflineIndicator';
import { ScrollToTop } from './components/common/ScrollToTop';
import { PageViewTracker } from './components/common/PageViewTracker';
import { RouteLoadingFallback } from './components/common/RouteLoadingFallback';
import { MainLayout } from './components/layout/MainLayout';
import { initOfflineServices } from './services/offline';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  // 오프라인 서비스 초기화 및 모바일 뒤로가기 버튼 제어
  useEffect(() => {
    initOfflineServices({
      onUpdate: (_registration) => {
        // 선택: 사용자에게 업데이트 알림 표시
      },
      onError: (error) => {
        console.error('[App] Service Worker 등록 실패:', error);
      },
    });

    // Capacitor 하드웨어 백버튼 제어
    const backButtonListener = CapApp.addListener('backButton', () => {
      const path = window.location.pathname;
      
      // 메인 홈, 로그인, 맵 대륙 등 최상위 루트 페이지에서는 뒤로가기 시 로그인창으로 튕기지 않고 앱 종료/백그라운드 전환
      if (path === '/home' || path === '/login' || path.startsWith('/map') || path === '/') {
        CapApp.exitApp();
      } else {
        // 그 외의 하위 페이지에서는 브라우저 히스토리 백 (안전한 뒤로가기)
        window.history.back();
      }
    });

    return () => {
      backButtonListener.then(l => l.remove());
    };
  }, []);

  return (
    <Provider store={store}>
      <PersistGate
        loading={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>}
        persistor={persistor}
      >
        <ThemeProvider>
          <LanguageProvider>
            <BaralProvider>
              <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                  {/* 라우트 이동 시 스크롤 최상단 리셋 (상단 잘림 방지) */}
                  <ScrollToTop />
                  {/* 페이지 조회수 자동 추적 */}
                  <PageViewTracker />
                  {/* 오프라인 상태 표시 */}
                  <OfflineIndicator />
                  <Suspense fallback={<RouteLoadingFallback fullScreen />}>
                  <Routes>
                    {/* Landing - 루트 진입점. 비로그인은 랜딩, 로그인 상태면 /home으로 */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute requireAuth={false} redirectTo="/home">
                          <Landing />
                        </ProtectedRoute>
                      }
                    />

                    {/* Public routes - 로그인하지 않은 사용자만 접근 가능 */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/find-account" element={<FindAccount />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Onboarding routes - 로그인한 사용자의 온보딩 */}
                    <Route
                      path="/onboarding/goal"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/login">
                          <GoalSelect />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/onboarding/level-test"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/login">
                          <InitialLevelTest />
                        </ProtectedRoute>
                      }
                    />

                    {/* Payment Callback Routes */}
                    <Route
                      path="/payment/success"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/login">
                          <PaymentSuccess />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/payment/fail"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/login">
                          <PaymentFail />
                        </ProtectedRoute>
                      }
                    />

                    {/* Protected routes - 로그인한 사용자만 접근 가능 */}
                    <Route element={<MainLayout />}>
                      <Route
                        path="/home"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Home />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/character/create"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <CharacterCreate />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/map"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Map />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/map/:attribute/:chapterId/:stageId"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <StageDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/workout/record"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <WorkoutRecord />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/workout/history"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <WorkoutHistoryPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/workout/stats"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <WorkoutStatsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/body-twin"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <BodyTwin />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/curriculum/week/0"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Week0Page />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/curriculum/week/1"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Week1Page />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/curriculum/week/:weekNumber/module/:moduleId"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <ModuleDetailPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/skills"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Skills />
                          </ProtectedRoute>
                        }
                      />
                      {/* Legacy /skill-tree → /skills redirect */}
                      <Route path="/skill-tree" element={<Navigate to="/skills" replace />} />
                      <Route
                        path="/grade-info"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <GradeInfo />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile/settings"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Settings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings/custom-exercises"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <CustomExercises />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings/data-transfer"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <DataTransfer />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <CharacterProfile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/u/:username"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <PublicProfile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile/edit"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <ProfileEdit />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/level-test/result"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <LevelTestResult />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/boards"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <BoardsHome />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/boards/free"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <FreeBoard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/boards/notice"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <NoticeBoard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/boards/map/:mapType/:chapterId"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <MapBoard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/boards/posts/:postId"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <BoardPostDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/achievements"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Achievements />
                          </ProtectedRoute>
                        }
                      />

                      {/* Shop Route */}
                      <Route
                        path="/shop"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Shop />
                          </ProtectedRoute>
                        }
                      />

                      {/* Charge Points Route */}
                      <Route
                        path="/charge-points"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <ChargePoints />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/leaderboard"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Leaderboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/gym/register"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <GymRegister />
                          </ProtectedRoute>
                        }
                      />

                      {/* Fragment Collection Route */}
                      <Route
                        path="/fragments"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Fragments />
                          </ProtectedRoute>
                        }
                      />

                      {/* Program Routes */}
                      <Route
                        path="/programs"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <Programs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/programs/:programId"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <ProgramDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/my-program"
                        element={
                          <ProtectedRoute requireAuth={true} redirectTo="/login">
                            <MyProgram />
                          </ProtectedRoute>
                        }
                      />

                    </Route>

                    {/* Trainer Routes */}
                    <Route
                      path="/trainer/login"
                      element={
                        <ProtectedRoute requireAuth={false} redirectTo="/trainer/dashboard">
                          <TrainerLogin />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/dashboard"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/trainer/login">
                          <TrainerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/programs/:id/subscribers"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/trainer/login">
                          <SubscriberList />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Routes */}
                    <Route
                      path="/admin/login"
                      element={
                        <ProtectedRoute requireAuth={false} redirectTo="/admin/dashboard">
                          <AdminLogin />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/dashboard"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <AdminDashboard />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/trainers"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <TrainerVerification />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <UserManagement />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/catalog"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <ProductCatalogManagement />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/fragment-redemptions"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <FragmentRedemptionManagement />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/map"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <MapConfig />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/payments"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <PaymentManagement />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/skill-reviews"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/admin/login">
                          <AdminGuard>
                            <AdminSkillReviewPage />
                          </AdminGuard>
                        </ProtectedRoute>
                      }
                    />

                    {/* Video Analysis Routes */}
                    <Route
                      path="/video-analysis"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/login">
                          <VideoAnalysisPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/video-analysis/:analysisId"
                      element={
                        <ProtectedRoute requireAuth={true} redirectTo="/login">
                          <VideoAnalysisDetail />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all - 404 redirect */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                  </Suspense>
                </BrowserRouter>
              </QueryClientProvider>
            </BaralProvider>
          </LanguageProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
